import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, BookOpen, CreditCard, Send, Plus, TrendingUp, Mail, UserRound, Phone, MapPin, Download, Bell, BellOff, Loader2, GitMerge, KeyRound, ImagePlus, Pencil, Trash2, ExternalLink } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { pushSupported, getCurrentSubscription, subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { Checkbox } from "@/components/ui/checkbox";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATES = ["Idea", "Questionario", "Struttura", "Scrittura", "Revisione", "Copertina", "Impaginazione", "Pubblicazione", "Amazon", "Royalty"];

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

const LEAD_STATUS = {
  da_contattare: { label: "Da contattare", cls: "bg-orange-400/10 text-orange-300 border-orange-400/30" },
  in_corso: { label: "In corso", cls: "bg-blue-400/10 text-blue-300 border-blue-400/30" },
  cliente: { label: "Cliente", cls: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30" },
};
const leadStatusLabel = (s) => (LEAD_STATUS[s] || LEAD_STATUS.da_contattare);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-white/5 bg-[#0A1124] p-6">
    <div className="flex items-center gap-2 text-white/40">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-xs uppercase tracking-widest">{label}</span>
    </div>
    <p className="mt-3 font-serif text-4xl font-light text-white">{value}</p>
  </div>
);

const PushToggleCard = () => {
  const [state, setState] = useState("idle"); // idle | subscribed | not-subscribed | unsupported | busy | denied
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!pushSupported()) { setState("unsupported"); return; }
      if (Notification.permission === "denied") { setState("denied"); return; }
      const sub = await getCurrentSubscription();
      setState(sub ? "subscribed" : "not-subscribed");
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setState("subscribed");
      toast.success("Notifiche push attive su questo dispositivo");
      // fire a test notification so the user immediately sees it work
      try { await api.post("/push/test"); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
      if (Notification.permission === "denied") setState("denied");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setState("not-subscribed");
      toast.success("Notifiche push disattivate su questo dispositivo");
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Notifica di prova inviata");
    } catch (e) {
      toast.error("Impossibile inviare la notifica di prova");
    } finally { setBusy(false); }
  };

  if (state === "unsupported") {
    return (
      <div className="rounded-2xl border border-white/5 bg-[#0A1124] p-6" data-testid="push-card">
        <div className="flex items-start gap-3">
          <BellOff className="mt-1 h-5 w-5 text-white/40" />
          <div>
            <p className="font-serif text-lg text-white">Notifiche push non disponibili</p>
            <p className="mt-1 text-sm text-white/50">
              Questo browser non supporta le notifiche push. Per riceverle sul telefono, apri il sito da Chrome/Edge (Android) o installalo dalla schermata Home (iOS 16.4+).
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-6" data-testid="push-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">
            <Bell className="h-5 w-5" />
          </span>
          <div>
            <p className="font-serif text-lg text-white">Notifiche in tempo reale sul telefono</p>
            <p className="mt-1 text-sm text-white/60">
              {state === "subscribed"
                ? "Attive su questo dispositivo. Riceverai un avviso su nuovo lead, autore, questionario, pagamento e messaggio in chat."
                : state === "denied"
                ? "Hai bloccato le notifiche da questo browser. Sbloccale dalle impostazioni del browser e ricarica la pagina."
                : "Attivale una volta e ricevi un avviso su nuovo lead, autore, questionario, pagamento e messaggio in chat."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {state === "subscribed" ? (
            <>
              <Button onClick={sendTest} disabled={busy} data-testid="push-test-btn"
                variant="outline"
                className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Prova
              </Button>
              <Button onClick={disable} disabled={busy} data-testid="push-disable-btn"
                variant="outline"
                className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                <BellOff className="h-4 w-4" /> Disattiva
              </Button>
            </>
          ) : (
            <Button onClick={enable} disabled={busy || state === "denied"} data-testid="push-enable-btn"
              className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Attiva notifiche
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState([]);
  const [active, setActive] = useState(null); // book being managed
  const [authors, setAuthors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadView, setLeadView] = useState(null);
  const [selectedAuthors, setSelectedAuthors] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);

  // form state
  const [msg, setMsg] = useState("");
  const [msgState, setMsgState] = useState("");
  const [chNum, setChNum] = useState("");
  const [chTitle, setChTitle] = useState("");
  const [chContent, setChContent] = useState("");
  const [royMonth, setRoyMonth] = useState("");
  const [royUnits, setRoyUnits] = useState("");
  const [royRev, setRoyRev] = useState("");

  // home releases ("Ultime uscite" in landing)
  const [releases, setReleases] = useState([]);
  const [relVer, setRelVer] = useState(0);
  const [relTitle, setRelTitle] = useState("");
  const [relAmazon, setRelAmazon] = useState("");
  const [relCover, setRelCover] = useState(null);
  const [relBusy, setRelBusy] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [editRelTitle, setEditRelTitle] = useState("");
  const [editRelAmazon, setEditRelAmazon] = useState("");
  const [editRelCover, setEditRelCover] = useState(null);
  const [editRelBusy, setEditRelBusy] = useState(false);
  const relCoverRef = useRef(null);
  const editRelCoverRef = useRef(null);

  const load = async () => {
    try {
      const [s, b, a, l, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/books"),
        api.get("/admin/authors"),
        api.get("/admin/leads"),
        api.get("/home-releases"),
      ]);
      setStats(s.data);
      setBooks(b.data.books);
      setAuthors(a.data.authors);
      setLeads(l.data.leads);
      setReleases(r.data.releases);
      setRelVer(Date.now());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === leadId ? { ...l, status } : l)));
      setLeadView((v) => (v && v.lead_id === leadId ? { ...v, status } : v));
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async (kind) => {
    try {
      const res = await api.get(`/admin/${kind}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = kind === "leads" ? "lead-selfbook.csv" : "autori-selfbook.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch {
      toast.error("Esportazione non riuscita");
    }
  };

  const createRelease = async () => {
    if (!relTitle.trim()) return toast.error("Inserisci il titolo del libro");
    if (!relAmazon.trim()) return toast.error("Inserisci il link Amazon");
    if (!relCover) return toast.error("Scegli l'immagine di copertina");
    setRelBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", relTitle);
      fd.append("amazon_url", relAmazon);
      fd.append("cover", relCover);
      await api.post("/admin/home-releases", fd);
      toast.success("Uscita aggiunta alla home");
      setRelTitle("");
      setRelAmazon("");
      setRelCover(null);
      if (relCoverRef.current) relCoverRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRelBusy(false);
    }
  };

  const openEditRelease = (r) => {
    setEditingRelease(r);
    setEditRelTitle(r.title);
    setEditRelAmazon(r.amazon_url);
    setEditRelCover(null);
  };

  const saveRelease = async () => {
    if (!editingRelease) return;
    setEditRelBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", editRelTitle);
      fd.append("amazon_url", editRelAmazon);
      if (editRelCover) fd.append("cover", editRelCover);
      await api.put(`/admin/home-releases/${editingRelease.release_id}`, fd);
      toast.success("Uscita aggiornata");
      setEditingRelease(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEditRelBusy(false);
    }
  };

  const deleteRelease = async (r) => {
    try {
      await api.delete(`/admin/home-releases/${r.release_id}`);
      toast.success("Uscita rimossa dalla home");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };


  const sendUpdate = async () => {
    if (!msg.trim()) return toast.error("Scrivi un messaggio");
    try {
      await api.post(`/books/${active.book_id}/updates`, { message: msg, state: msgState || null });
      toast.success("Aggiornamento inviato all'autore");
      setMsg("");
      setMsgState("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addChapter = async () => {
    try {
      await api.post(`/books/${active.book_id}/chapters`, {
        number: parseInt(chNum, 10) || 1,
        title: chTitle || `Capitolo ${chNum || 1}`,
        content: chContent || "Bozza in lavorazione.",
      });
      toast.success("Capitolo inviato per l'approvazione");
      setChNum("");
      setChTitle("");
      setChContent("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addRoyalty = async () => {
    try {
      await api.post(`/books/${active.book_id}/royalties`, {
        month: royMonth || "2025-12",
        units: parseInt(royUnits, 10) || 0,
        revenue: parseFloat(royRev) || 0,
      });
      toast.success("Royalty registrata");
      setRoyMonth("");
      setRoyUnits("");
      setRoyRev("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Pannello team</p>
        <h1 className="mt-3 font-serif text-4xl font-light text-white sm:text-5xl">Regia editoriale</h1>
        <p className="mt-2 text-white/50">Gestisci tutti gli autori e i loro progetti.</p>

        <div className="mt-8">
          <PushToggleCard />
        </div>

        {stats && (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={BookOpen} label="Libri totali" value={stats.total_books} />
            <StatCard icon={Users} label="Autori" value={stats.total_authors} />
            <StatCard icon={Mail} label="Lead landing" value={stats.total_leads ?? 0} />
            <StatCard icon={CreditCard} label="Ordini pagati" value={stats.paid_orders} />
          </div>
        )}

        {/* Contatti: Autori registrati */}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-light text-white">Autori registrati</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/30">{authors.length} contatti</span>
            {selectedAuthors.size >= 2 && (
              <Button size="sm" onClick={() => { setMergeTarget(Array.from(selectedAuthors)[0]); setMergeOpen(true); }}
                data-testid="merge-authors-btn"
                className="gap-1.5 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                <GitMerge className="h-3.5 w-3.5" /> Unisci {selectedAuthors.size} account
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => exportCsv("authors")} data-testid="export-authors-btn" className="gap-1.5 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </div>
        <div className="mt-6 rounded-2xl border border-white/5 bg-[#0A1124] md:overflow-x-auto" data-testid="authors-table">
          <table className="block w-full text-left text-sm md:table md:min-w-[600px]">
            <thead className="hidden md:table-header-group">
              <tr className="border-b border-white/5 text-white/40">
                <th className="p-4 w-8"></th>
                <th className="p-4 font-normal">Nome</th>
                <th className="p-4 font-normal">Email</th>
                <th className="p-4 font-normal">Registrazione</th>
                <th className="p-4 font-normal">Iscritto il</th>
                <th className="p-4 text-center font-normal">Libri</th>
                <th className="p-4 text-right font-normal">Azioni</th>
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {authors.length === 0 ? (
                <tr className="block md:table-row"><td colSpan={7} className="block w-full p-6 text-center text-white/40 md:table-cell">Nessun autore registrato.</td></tr>
              ) : authors.map((a) => (
                <tr key={a.user_id} className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-white/5 p-4 last:border-0 md:table-row md:p-0" data-testid={`author-row-${a.user_id}`}>
                  <td className="order-1 md:table-cell md:p-4">
                    <Checkbox
                      checked={selectedAuthors.has(a.user_id)}
                      onCheckedChange={(v) => {
                        const s = new Set(selectedAuthors);
                        if (v) s.add(a.user_id); else s.delete(a.user_id);
                        setSelectedAuthors(s);
                      }}
                      data-testid={`author-check-${a.user_id}`}
                      className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                    />
                  </td>
                  <td className="order-2 font-medium text-white md:table-cell md:p-4 md:font-normal">{a.name || "—"}</td>
                  <td className="order-5 w-full break-all text-white/70 md:table-cell md:w-auto md:p-4">{a.email}</td>
                  <td className="order-3 md:table-cell md:p-4">
                    <span className={`rounded-full px-2.5 py-1 text-xs ${a.auth_provider === "google" ? "bg-blue-400/10 text-blue-300" : "bg-white/5 text-white/60"}`}>
                      {a.auth_provider === "google" ? "Google" : "Email"}
                    </span>
                  </td>
                  <td className="order-6 text-xs text-white/50 md:table-cell md:p-4 md:text-sm">
                    <span className="mr-1.5 text-[10px] uppercase tracking-wider text-white/30 md:hidden">Iscritto il</span>
                    {fmtDate(a.created_at)}
                  </td>
                  <td className="order-7 text-xs text-[#D4AF37] md:table-cell md:p-4 md:text-center md:text-sm">
                    <span className="mr-1.5 text-[10px] uppercase tracking-wider text-white/30 md:hidden">Libri</span>
                    {a.book_count}
                  </td>
                  <td className="order-4 ml-auto md:table-cell md:p-4 md:text-right">
                    {a.auth_provider !== "google" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/admin/authors/${a.user_id}/reset-link`);
                            await navigator.clipboard.writeText(data.link);
                            toast.success("Link reset password copiato negli appunti (valido 1 ora)");
                          } catch (e) {
                            toast.error(formatApiError(e.response?.data?.detail));
                          }
                        }}
                        data-testid={`author-reset-link-${a.user_id}`}
                        className="h-7 gap-1 rounded-full border-white/10 bg-transparent px-2.5 text-[11px] text-white/60 hover:bg-white/5 hover:text-[#D4AF37]"
                        title="Genera link reset password (valido 1h)"
                      >
                        <KeyRound className="h-3 w-3" /> Reset
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Edit home release dialog */}
        <Dialog open={!!editingRelease} onOpenChange={(o) => !o && setEditingRelease(null)}>
          <DialogContent className="border-white/10 bg-[#0A1124] text-white" data-testid="edit-release-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-light">Modifica uscita</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3 pt-2">
              <div>
                <Label className="text-white/60">Titolo del libro</Label>
                <Input value={editRelTitle} onChange={(e) => setEditRelTitle(e.target.value)} data-testid="edit-release-title"
                  className="mt-1.5 border-white/10 bg-white/5 text-white" />
              </div>
              <div>
                <Label className="text-white/60">Link Amazon</Label>
                <Input value={editRelAmazon} onChange={(e) => setEditRelAmazon(e.target.value)} data-testid="edit-release-amazon"
                  className="mt-1.5 border-white/10 bg-white/5 text-white" />
              </div>
              <div>
                <Label className="text-white/60">Copertina (lascia vuoto per mantenere l'attuale)</Label>
                <input ref={editRelCoverRef} type="file" accept="image/*" className="hidden" data-testid="edit-release-cover-input"
                  onChange={(e) => setEditRelCover(e.target.files?.[0] || null)} />
                <div className="mt-1.5 flex items-center gap-3">
                  <Button type="button" variant="outline" onClick={() => editRelCoverRef.current?.click()} data-testid="edit-release-cover-btn"
                    className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5 hover:text-white">
                    <ImagePlus className="h-4 w-4" /> {editRelCover ? editRelCover.name : "Cambia copertina"}
                  </Button>
                  {editRelCover && (
                    <img src={URL.createObjectURL(editRelCover)} alt="Anteprima nuova copertina"
                      className="h-14 w-10 rounded border border-white/10 object-cover" />
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditingRelease(null)} className="rounded-full text-white/60">Annulla</Button>
                <Button onClick={saveRelease} disabled={editRelBusy} data-testid="edit-release-save"
                  className="rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                  {editRelBusy ? "Salvataggio…" : "Salva modifiche"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Merge authors dialog */}
        <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
          <DialogContent className="max-w-lg border-white/10 bg-[#0A1124] text-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-light">Unisci account autore</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-white/60">
                Scegli l'account <strong className="text-white">principale</strong>: tutti i libri, chat, documenti e
                pagamenti degli altri account verranno spostati qui. Gli account non selezionati saranno disattivati
                (non potranno più fare login), ma la storia rimane consultabile.
              </p>
              <div className="rounded-xl border border-white/10 bg-white/[0.02]">
                {Array.from(selectedAuthors).map((uid) => {
                  const a = authors.find((x) => x.user_id === uid);
                  if (!a) return null;
                  const isTarget = mergeTarget === uid;
                  return (
                    <label key={uid} className={`flex cursor-pointer items-center gap-3 border-b border-white/5 p-3 last:border-0 ${isTarget ? "bg-[#D4AF37]/10" : "hover:bg-white/[0.02]"}`}>
                      <input type="radio" name="merge-target" checked={isTarget}
                        onChange={() => setMergeTarget(uid)}
                        data-testid={`merge-target-${uid}`}
                        className="h-4 w-4 accent-[#D4AF37]" />
                      <div className="flex-1">
                        <p className="text-white">{a.name || "—"}</p>
                        <p className="text-xs text-white/50">{a.email} · {a.book_count} libri</p>
                      </div>
                      {isTarget && <span className="rounded-full bg-[#D4AF37]/20 px-2 py-0.5 text-[10px] text-[#D4AF37]">principale</span>}
                    </label>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMergeOpen(false)}
                className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                Annulla
              </Button>
              <Button
                onClick={async () => {
                  if (!mergeTarget) return;
                  const sources = Array.from(selectedAuthors).filter((u) => u !== mergeTarget);
                  if (!sources.length) { toast.error("Seleziona almeno 2 account e scegli il principale"); return; }
                  if (!window.confirm(`Confermi la fusione? ${sources.length} account verranno disattivati e il loro contenuto spostato sull'account principale. Non è reversibile.`)) return;
                  setMergeBusy(true);
                  try {
                    const { data } = await api.post("/admin/authors/merge", {
                      target_user_id: mergeTarget, source_user_ids: sources,
                    });
                    toast.success(`Fusione completata: ${data.moved.books} libri, ${data.moved.messages} messaggi, ${data.moved.payments} pagamenti spostati.`);
                    setSelectedAuthors(new Set());
                    setMergeOpen(false);
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                  } finally { setMergeBusy(false); }
                }}
                disabled={mergeBusy || !mergeTarget}
                data-testid="merge-confirm-btn"
                className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                {mergeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
                Unisci
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contatti: Lead dalla landing */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-light text-white">Lead dalla landing</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/30">{leads.length} richieste</span>
            <Button size="sm" variant="outline" onClick={() => exportCsv("leads")} data-testid="export-leads-btn" className="gap-1.5 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-2" data-testid="leads-list">
          {leads.length === 0 ? (
            <p className="rounded-xl border border-dashed border-white/10 bg-[#0A1124] p-6 text-center text-white/40">
              Ancora nessuna richiesta di analisi gratuita dalla landing.
            </p>
          ) : leads.map((l) => (
            <div key={l.lead_id} className="flex flex-col gap-3 rounded-xl border border-white/5 bg-[#0A1124] px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`lead-row-${l.lead_id}`}>
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37]"><UserRound className="h-4 w-4" /></span>
                <div>
                  <p className="text-white">{l.name}</p>
                  <p className="text-xs text-white/40">{l.email}{l.phone ? ` · ${l.phone}` : ""}{l.city ? ` · ${l.city}` : ""}</p>
                  <p className="text-[11px] text-white/25">{fmtDate(l.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={l.status || "da_contattare"} onValueChange={(v) => updateLeadStatus(l.lead_id, v)}>
                  <SelectTrigger data-testid={`lead-status-${l.lead_id}`} className={`h-8 w-40 rounded-full border text-xs ${leadStatusLabel(l.status).cls}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0A1124] text-white">
                    {Object.entries(LEAD_STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="focus:bg-white/5 focus:text-white">{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => setLeadView(l)} data-testid={`lead-view-${l.lead_id}`} className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                  Vedi risposte
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Home releases ("Ultime uscite" in landing) */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-light text-white">Ultime uscite in home</h2>
          <p className="text-sm text-white/40" data-testid="releases-count">{releases.length} in vetrina</p>
        </div>
        <div className="mt-6 rounded-xl border border-white/5 bg-[#0A1124] p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-white/60">Titolo del libro</Label>
              <Input value={relTitle} onChange={(e) => setRelTitle(e.target.value)} data-testid="release-title-input"
                className="mt-1.5 border-white/10 bg-white/5 text-white" placeholder="Es. La musica è vita" />
            </div>
            <div>
              <Label className="text-white/60">Link Amazon</Label>
              <Input value={relAmazon} onChange={(e) => setRelAmazon(e.target.value)} data-testid="release-amazon-input"
                className="mt-1.5 border-white/10 bg-white/5 text-white" placeholder="https://www.amazon.it/dp/…" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input ref={relCoverRef} type="file" accept="image/*" className="hidden" data-testid="release-cover-input"
              onChange={(e) => setRelCover(e.target.files?.[0] || null)} />
            <Button type="button" variant="outline" onClick={() => relCoverRef.current?.click()} data-testid="release-cover-btn"
              className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5 hover:text-white">
              <ImagePlus className="h-4 w-4" /> {relCover ? relCover.name : "Scegli copertina"}
            </Button>
            {relCover && (
              <img src={URL.createObjectURL(relCover)} alt="Anteprima copertina" data-testid="release-cover-preview"
                className="h-16 w-12 rounded border border-white/10 object-cover" />
            )}
            <Button onClick={createRelease} disabled={relBusy} data-testid="release-add-btn"
              className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
              {relBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Aggiungi uscita
            </Button>
          </div>
        </div>
        <div className="mt-4 space-y-3" data-testid="releases-admin-list">
          {releases.length === 0 ? (
            <p className="text-sm text-white/40">Nessuna uscita in vetrina. Aggiungi la prima qui sopra: apparirà nella home del sito.</p>
          ) : releases.map((r) => (
            <div key={r.release_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-white/5 bg-[#0A1124] p-4" data-testid={`admin-release-${r.release_id}`}>
              <img src={`${API}/home-releases/${r.release_id}/cover?v=${relVer}`} alt={`Copertina di ${r.title}`}
                className="h-20 w-14 rounded border border-white/10 object-cover" data-testid={`admin-release-cover-${r.release_id}`} />
              <div className="min-w-0 flex-1 basis-40">
                <p className="text-white">{r.title}</p>
                <a href={r.amazon_url} target="_blank" rel="noopener noreferrer"
                  className="mt-1 flex items-center gap-1 break-all text-xs text-[#D4AF37]/80 hover:text-[#D4AF37]"
                  data-testid={`admin-release-link-${r.release_id}`}>
                  <ExternalLink className="h-3 w-3 shrink-0" /> {r.amazon_url}
                </a>
              </div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEditRelease(r)} data-testid={`release-edit-${r.release_id}`}
                  className="h-8 gap-1.5 rounded-full border-white/10 bg-transparent px-3 text-xs text-white/70 hover:bg-white/5 hover:text-white">
                  <Pencil className="h-3 w-3" /> Modifica
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteRelease(r)} data-testid={`release-delete-${r.release_id}`}
                  className="h-8 gap-1.5 rounded-full border-red-400/20 bg-transparent px-3 text-xs text-red-300 hover:bg-red-400/10">
                  <Trash2 className="h-3 w-3" /> Elimina
                </Button>
              </div>
            </div>
          ))}
        </div>


        <h2 className="mt-14 font-serif text-2xl font-light text-white">Tutti i progetti</h2>
        <div className="mt-6 flex flex-col gap-2">
          {books.map((b) => (
            <div key={b.book_id} className="flex flex-col gap-3 rounded-xl border border-white/5 bg-[#0A1124] px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-book-${b.book_id}`}>
              <div>
                <p className="text-white">{b.title}</p>
                <p className="text-xs text-white/40">{b.owner_name} · <span className="text-[#D4AF37]">{b.status}</span></p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/books/${b.book_id}`)} data-testid={`admin-view-${b.book_id}`} className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                  Apri
                </Button>
                <Button size="sm" onClick={() => setActive(b)} data-testid={`admin-manage-${b.book_id}`} className="rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                  Gestisci
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-[#0A1124] text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">{active?.title}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="update">
            <TabsList className="grid w-full grid-cols-3 bg-white/5">
              <TabsTrigger value="update" data-testid="admin-tab-update" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050A15]">Aggiorna</TabsTrigger>
              <TabsTrigger value="chapter" data-testid="admin-tab-chapter" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050A15]">Capitolo</TabsTrigger>
              <TabsTrigger value="royalty" data-testid="admin-tab-royalty" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050A15]">Royalty</TabsTrigger>
            </TabsList>

            <TabsContent value="update" className="mt-4 flex flex-col gap-3">
              <Label className="text-white/70">Messaggio all'autore</Label>
              <Textarea data-testid="admin-update-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="border-white/10 bg-white/5 text-white" placeholder="Es. I primi capitoli sono pronti…" />
              <Label className="text-white/70">Avanza lo stato (opzionale)</Label>
              <Select value={msgState} onValueChange={setMsgState}>
                <SelectTrigger data-testid="admin-update-state" className="border-white/10 bg-white/5 text-white"><SelectValue placeholder="Nessun cambio di stato" /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#0A1124] text-white">
                  {STATES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={sendUpdate} data-testid="admin-send-update-btn" className="mt-2 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"><Send className="h-4 w-4" /> Invia aggiornamento</Button>
            </TabsContent>

            <TabsContent value="chapter" className="mt-4 flex flex-col gap-3">
              <Label className="text-white/70">Numero capitolo</Label>
              <Input data-testid="admin-chapter-number" type="number" value={chNum} onChange={(e) => setChNum(e.target.value)} className="border-white/10 bg-white/5 text-white" placeholder="1" />
              <Label className="text-white/70">Titolo</Label>
              <Input data-testid="admin-chapter-title" value={chTitle} onChange={(e) => setChTitle(e.target.value)} className="border-white/10 bg-white/5 text-white" placeholder="Titolo del capitolo" />
              <Label className="text-white/70">Contenuto (anteprima)</Label>
              <Textarea data-testid="admin-chapter-content" value={chContent} onChange={(e) => setChContent(e.target.value)} rows={3} className="border-white/10 bg-white/5 text-white" />
              <Button onClick={addChapter} data-testid="admin-add-chapter-btn" className="mt-2 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"><Plus className="h-4 w-4" /> Invia capitolo</Button>
            </TabsContent>

            <TabsContent value="royalty" className="mt-4 flex flex-col gap-3">
              <Label className="text-white/70">Mese (YYYY-MM)</Label>
              <Input data-testid="admin-royalty-month" value={royMonth} onChange={(e) => setRoyMonth(e.target.value)} className="border-white/10 bg-white/5 text-white" placeholder="2025-12" />
              <Label className="text-white/70">Copie vendute</Label>
              <Input data-testid="admin-royalty-units" type="number" value={royUnits} onChange={(e) => setRoyUnits(e.target.value)} className="border-white/10 bg-white/5 text-white" placeholder="100" />
              <Label className="text-white/70">Royalty (€)</Label>
              <Input data-testid="admin-royalty-revenue" type="number" value={royRev} onChange={(e) => setRoyRev(e.target.value)} className="border-white/10 bg-white/5 text-white" placeholder="500" />
              <Button onClick={addRoyalty} data-testid="admin-add-royalty-btn" className="mt-2 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"><TrendingUp className="h-4 w-4" /> Registra royalty</Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={!!leadView} onOpenChange={(o) => !o && setLeadView(null)}>
        <DialogContent className="border-white/10 bg-[#0A1124] text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">{leadView?.name}</DialogTitle>
          </DialogHeader>
          {leadView && (
            <div className="flex flex-col gap-4">
              <span className={`w-fit rounded-full border px-3 py-1 text-xs ${leadStatusLabel(leadView.status).cls}`} data-testid="lead-view-status">
                {leadStatusLabel(leadView.status).label}
              </span>
              <div className="grid gap-2 text-sm">
                <p className="flex items-center gap-2 text-white/70"><Mail className="h-4 w-4 text-[#D4AF37]" /> {leadView.email}</p>
                {leadView.phone && <p className="flex items-center gap-2 text-white/70"><Phone className="h-4 w-4 text-[#D4AF37]" /> {leadView.phone}</p>}
                {leadView.city && <p className="flex items-center gap-2 text-white/70"><MapPin className="h-4 w-4 text-[#D4AF37]" /> {leadView.city}</p>}
                {leadView.profession && <p className="flex items-center gap-2 text-white/70"><UserRound className="h-4 w-4 text-[#D4AF37]" /> {leadView.profession}</p>}
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="mb-3 text-xs uppercase tracking-widest text-white/40">Risposte</p>
                <div className="flex flex-col gap-3">
                  {Object.entries(leadView.answers || {}).map(([q, a]) => (
                    <div key={q}>
                      <p className="text-sm text-[#D4AF37]">{q}</p>
                      <p className="text-sm text-white/70">{a || "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
              <a
                href={`mailto:${leadView.email}`}
                data-testid="lead-email-link"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm text-[#050A15] hover:bg-[#E5C158]"
              >
                <Mail className="h-4 w-4" /> Rispondi via email
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
